import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  RegulatoryRequirement,
  ApplicabilityStatus,
  RelationshipType,
  RelationshipStrength,
} from '../types';
import { regulatoryService } from '../regulatoryService';

interface RequirementDetailModalProps {
  requirement: RegulatoryRequirement;
  onClose: () => void;
  onRefresh: () => void;
}

export const RequirementDetailModal: React.FC<RequirementDetailModalProps> = ({
  requirement: initialReq,
  onClose,
  onRefresh,
}) => {
  const [requirement, setRequirement] = useState<RegulatoryRequirement>(initialReq);
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'APPLICABILITY' | 'CROSSWALK' | 'CONTROLS'>('OVERVIEW');
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Applicability Form
  const [appStatus, setAppStatus] = useState<ApplicabilityStatus>(
    initialReq.organizationApplicability?.applicabilityStatus || 'APPLICABLE'
  );
  const [whyApp, setWhyApp] = useState(initialReq.organizationApplicability?.whyApplicable || '');
  const [whyNotApp, setWhyNotApp] = useState(initialReq.organizationApplicability?.whyNotApplicable || '');
  const [conditions, setConditions] = useState(initialReq.organizationApplicability?.conditions || '');
  const [reviewerName, setReviewerName] = useState(initialReq.organizationApplicability?.reviewerName || 'Compliance Manager');

  // Control Mapping Form
  const [showAddControl, setShowAddControl] = useState(false);
  const [ctrlRef, setCtrlRef] = useState('');
  const [ctrlTitle, setCtrlTitle] = useState('');
  const [ctrlType, setCtrlType] = useState('PROCEDURE');
  const [complianceLevel, setComplianceLevel] = useState<'COMPLIANT' | 'PARTIALLY_COMPLIANT' | 'NON_COMPLIANT' | 'PLANNED'>('COMPLIANT');

  // Crosswalk Form
  const [showAddCrosswalk, setShowAddCrosswalk] = useState(false);
  const [targetReqId, setTargetReqId] = useState('');
  const [relType, setRelType] = useState<RelationshipType>('EQUIVALENT');
  const [relStrength, setRelStrength] = useState<RelationshipStrength>('FULL');
  const [relNotes, setRelNotes] = useState('');

  useEffect(() => {
    loadFullDetail();
  }, [initialReq.id]);

  const loadFullDetail = async () => {
    try {
      setLoading(true);
      const full = await regulatoryService.getRequirementById(initialReq.id);
      setRequirement(full);
      if (full.organizationApplicability) {
        setAppStatus(full.organizationApplicability.applicabilityStatus);
        setWhyApp(full.organizationApplicability.whyApplicable || '');
        setWhyNotApp(full.organizationApplicability.whyNotApplicable || '');
        setConditions(full.organizationApplicability.conditions || '');
      }
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveApplicability = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await regulatoryService.setOrganizationRequirementApplicability({
        organizationId: 'ORG-XYZ',
        requirementId: requirement.id,
        applicabilityStatus: appStatus,
        decisionSource: 'MANUAL',
        whyApplicable: whyApp,
        whyNotApplicable: whyNotApp,
        conditions: conditions || undefined,
        reviewerName,
      });
      setActionSuccess('Regulatory applicability decision and justification saved successfully.');
      await loadFullDetail();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleAddControlMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await regulatoryService.createControlMapping({
        organizationId: 'ORG-XYZ',
        requirementId: requirement.id,
        controlReference: ctrlRef,
        controlTitle: ctrlTitle,
        controlType: ctrlType,
        complianceLevel,
      });
      setCtrlRef('');
      setCtrlTitle('');
      setShowAddControl(false);
      setActionSuccess('Internal control and procedural mapping added successfully.');
      await loadFullDetail();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleAddCrosswalk = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await regulatoryService.apiRequest(`/compliance/regulatory/requirements/${requirement.id}/relationships`, {
        method: 'POST',
        body: JSON.stringify({
          targetRequirementId: targetReqId,
          relationshipType: relType,
          strength: relStrength,
          notes: relNotes,
        }),
      });
      setTargetReqId('');
      setRelNotes('');
      setShowAddCrosswalk(false);
      setActionSuccess('Cross-framework equivalence mapping established successfully.');
      await loadFullDetail();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: '#dbeafe', color: '#1e40af' }}>
                {requirement.requirementCode}
              </span>
              <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', background: '#f1f5f9', color: '#475569', fontWeight: 600 }}>
                {requirement.frameworkCode} • {requirement.documentCode}
              </span>
              {requirement.isMandatory && (
                <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: '#fee2e2', color: '#991b1b', fontWeight: 700 }}>
                  MANDATORY
                </span>
              )}
            </div>
            <h3>{requirement.title}</h3>
            <p>Regulatory requirement analysis, applicability assessment, and control mapping</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {/* Notifications */}
        {actionError && (
          <div style={{ margin: '16px 24px 0', padding: '12px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: '8px', fontSize: '13px' }}>
            <strong>Error:</strong> {actionError}
          </div>
        )}
        {actionSuccess && (
          <div style={{ margin: '16px 24px 0', padding: '12px', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: '8px', fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><strong>Success:</strong> {actionSuccess}</span>
            <button type="button" onClick={() => setActionSuccess(null)} style={{ background: 'none', border: 'none', color: '#065f46', fontWeight: 'bold', cursor: 'pointer' }}>✕</button>
          </div>
        )}

        {/* Tabs Bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color, #e2e8f0)', background: 'var(--bg-subtle, #f8fafc)', padding: '0 24px', gap: '8px' }}>
          {(['OVERVIEW', 'APPLICABILITY', 'CROSSWALK', 'CONTROLS'] as const).map((tab) => {
            const labels: Record<string, string> = {
              OVERVIEW: 'Normative Text & Scope',
              APPLICABILITY: `Applicability & Rationale (${requirement.organizationApplicability?.applicabilityStatus || 'NOT EVALUATED'})`,
              CROSSWALK: `Cross-Framework Equivalence (${requirement.relationships?.length || 0})`,
              CONTROLS: `Controls & Documentation (${requirement.controlMappings?.length || 0})`,
            };
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'var(--primary-color, #2563eb)' : 'var(--text-muted, #64748b)',
                  borderBottom: isActive ? '2px solid var(--primary-color, #2563eb)' : '2px solid transparent',
                  background: 'transparent',
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  cursor: 'pointer',
                }}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>

        {/* Tab Body */}
        <div className="modal-body">
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'OVERVIEW' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', padding: '16px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                  Official Regulatory Mandate Text
                </span>
                <p style={{ margin: 0, color: 'var(--text-primary, #0f172a)', lineHeight: 1.6, fontSize: '13px', background: 'var(--card-bg, #fff)', padding: '16px', borderRadius: '6px', border: '1px solid var(--border-color, #e2e8f0)' }}>
                  {requirement.requirementText}
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', background: 'var(--card-bg, #fff)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Regulatory Framework</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{requirement.frameworkName || requirement.frameworkCode}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hierarchy Path</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{requirement.nodePath || 'Root Level'}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type & Level</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{requirement.requirementType} ({requirement.normativeLevel})</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Effective Date</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{requirement.effectiveDate || '-'}</span>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: APPLICABILITY */}
          {activeTab === 'APPLICABILITY' && (
            <form onSubmit={handleSaveApplicability} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ padding: '12px 16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <strong style={{ fontSize: '13px', color: '#1e40af', display: 'block' }}>Operational Applicability Assessment</strong>
                <span style={{ fontSize: '12px', color: '#3b82f6' }}>Determine organizational applicability based on AOC scope, fleet type, and operating specifications.</span>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Applicability Status *</label>
                  <select
                    value={appStatus}
                    onChange={(e) => setAppStatus(e.target.value as any)}
                  >
                    <option value="APPLICABLE">Applicable (Mandatory for AOC)</option>
                    <option value="NOT_APPLICABLE">Not Applicable (Out of Operational Scope)</option>
                    <option value="CONDITIONALLY_APPLICABLE">Conditionally Applicable (Specific Fleet/Route)</option>
                    <option value="UNKNOWN">Pending Assessment / Unknown</option>
                  </select>
                </div>

                <div className="form-field">
                  <label>Reviewing Manager</label>
                  <input
                    type="text"
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Why Applicable? (Operational Justification)</label>
                <textarea
                  rows={2}
                  value={whyApp}
                  onChange={(e) => setWhyApp(e.target.value)}
                  placeholder="e.g. Applicable due to commercial air transport operations under EU-OPS and EASA Aircrew regulations..."
                />
              </div>

              <div className="form-field">
                <label>Why Not Applicable? (Exclusion Rationale)</label>
                <textarea
                  rows={2}
                  value={whyNotApp}
                  onChange={(e) => setWhyNotApp(e.target.value)}
                  placeholder="e.g. Fleet does not operate rotorcraft or single-engine turbine aircraft..."
                />
              </div>

              <div className="form-field">
                <label>Specific Conditions & Caveats</label>
                <input
                  type="text"
                  value={conditions}
                  onChange={(e) => setConditions(e.target.value)}
                  placeholder="e.g. Valid only for international ETOPS and oceanic flight operations."
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px' }}>
                <button type="submit" className="btn-primary">
                  Save Applicability Decision
                </button>
              </div>
            </form>
          )}

          {/* TAB 3: CROSSWALK */}
          {activeTab === 'CROSSWALK' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)', margin: 0 }}>Cross-Framework Equivalence</h4>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)', margin: '2px 0 0 0' }}>Equivalent relationships across EASA, ICAO, national CAAs, and IOSA standards.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddCrosswalk(!showAddCrosswalk)}
                  className="btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  {showAddCrosswalk ? 'Close Form' : '+ Add Equivalence Mapping'}
                </button>
              </div>

              {showAddCrosswalk && (
                <form onSubmit={handleAddCrosswalk} style={{ padding: '16px', background: 'var(--bg-subtle, #f8fafc)', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Target Requirement Code / ID *</label>
                      <input
                        type="text"
                        required
                        value={targetReqId}
                        onChange={(e) => setTargetReqId(e.target.value)}
                        placeholder="e.g. REQ-ICAO-SMS-3-1"
                      />
                    </div>
                    <div className="form-field">
                      <label>Relationship Type</label>
                      <select
                        value={relType}
                        onChange={(e) => setRelType(e.target.value as any)}
                      >
                        <option value="EQUIVALENT">Equivalent (Exact Match)</option>
                        <option value="COMPLEMENTARY">Complementary</option>
                        <option value="NATIONAL_DIFFERENCE">National Difference</option>
                        <option value="CONFLICTS_WITH">Conflicts With / Stricter Than</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Harmonization Notes</label>
                    <input
                      type="text"
                      value={relNotes}
                      onChange={(e) => setRelNotes(e.target.value)}
                      placeholder="Harmonization and compliance cross-reference details..."
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '4px' }}>
                    <button type="submit" className="btn-primary">
                      Record Cross-Reference
                    </button>
                  </div>
                </form>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {requirement.relationships && requirement.relationships.length > 0 ? (
                  requirement.relationships.map((rel) => (
                    <div key={rel.id} style={{ padding: '12px 16px', background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e40af', background: '#eff6ff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                            {rel.targetRequirementCode}
                          </span>
                          <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, background: '#dbeafe', color: '#1e40af' }}>
                            {rel.relationshipType} ({rel.strength})
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary, #0f172a)', marginTop: '4px' }}>{rel.targetRequirementTitle}</div>
                        {rel.notes && <div style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)', fontStyle: 'italic', marginTop: '2px' }}>{rel.notes}</div>}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', fontFamily: 'monospace', textAlign: 'right' }}>
                        Verified by: {rel.verifiedBy || 'System'}
                      </div>
                    </div>
                  ))
                ) : (
                  <p style={{ fontSize: '13px', color: 'var(--text-muted, #64748b)', textAlign: 'center', padding: '16px' }}>No cross-framework equivalence relationships defined.</p>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: CONTROLS */}
          {activeTab === 'CONTROLS' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)', margin: 0 }}>Internal Controls & Procedural Evidence</h4>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)', margin: '2px 0 0 0' }}>Internal company manual, SOP, software control, or training module addressing this requirement.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddControl(!showAddControl)}
                  className="btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  {showAddControl ? 'Close Form' : '+ Map Internal Procedure'}
                </button>
              </div>

              {showAddControl && (
                <form onSubmit={handleAddControlMapping} style={{ padding: '16px', background: 'var(--bg-subtle, #f8fafc)', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Control / Document Reference *</label>
                      <input
                        type="text"
                        required
                        value={ctrlRef}
                        onChange={(e) => setCtrlRef(e.target.value)}
                        placeholder="e.g. OMM Chapter 2.4 or SOP-OPS-12"
                      />
                    </div>
                    <div className="form-field">
                      <label>Control Type</label>
                      <select
                        value={ctrlType}
                        onChange={(e) => setCtrlType(e.target.value)}
                      >
                        <option value="PROCEDURE">Written Procedure / Manual</option>
                        <option value="SYSTEM_GUARD">Software / Automated Guardrail</option>
                        <option value="TRAINING">Personnel Training Syllabus</option>
                        <option value="AUDIT_CHECKLIST">Audit Checklist Verification</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Control Title & Operational Scope *</label>
                    <input
                      type="text"
                      required
                      value={ctrlTitle}
                      onChange={(e) => setCtrlTitle(e.target.value)}
                      placeholder="e.g. Operations Manual Part A - Fuel Management Policy"
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '4px' }}>
                    <button type="submit" className="btn-primary">
                      Save Control Mapping
                    </button>
                  </div>
                </form>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {requirement.controlMappings && requirement.controlMappings.length > 0 ? (
                  requirement.controlMappings.map((cm) => (
                    <div key={cm.id} style={{ padding: '12px 16px', background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary, #0f172a)', fontSize: '13px' }}>{cm.controlReference}</span>
                          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#ecfdf5', color: '#065f46' }}>
                            {cm.complianceLevel}
                          </span>
                          <span style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)' }}>({cm.controlType})</span>
                        </div>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary, #334155)' }}>{cm.controlTitle}</p>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', fontFamily: 'monospace', textAlign: 'right' }}>
                        Mapped by: {cm.mappedBy}
                      </div>
                    </div>
                  ))
                ) : (
                  <p style={{ fontSize: '13px', color: 'var(--text-muted, #64748b)', textAlign: 'center', padding: '16px' }}>No internal control mappings defined.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', fontFamily: 'monospace' }}>
            Requirement ID: {requirement.id}
          </span>
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
